import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CoverageIcon } from "@/components/benefits/v2/typeIcons";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

export type DentalRFPParameters = {
  coverageTypes: string[];
  networkType: string;
  maximumBenefit: number;
  customMaximum?: number;
  perioEndoCoverage: string;
  orthodonticCoverage: string;
  orthodonticMaximum: number;
  customOrthoMaximum?: number;
  specialMessage?: string;
};

type DentalRFPFormProps = {
  value: DentalRFPParameters;
  onChange: (value: DentalRFPParameters) => void;
};

const MAXIMUM_OPTIONS = [1000, 1500, 2000, 2500, 3000];
const ORTHO_MAXIMUM_OPTIONS = [1000, 1500, 2000, 2500, 3000];

export function DentalRFPForm({ value, onChange }: DentalRFPFormProps) {
  const updateField = (field: keyof DentalRFPParameters, newValue: any) => {
    onChange({ ...value, [field]: newValue });
  };

  const toggleCoverageType = (type: string) => {
    const current = value.coverageTypes || [];
    const updated = current.includes(type)
      ? current.filter(t => t !== type)
      : [...current, type];
    updateField('coverageTypes', updated);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CoverageIcon planType="dental" size="sm" />
          Dental
        </CardTitle>
        <p className="text-xs text-muted-foreground">What the dental plan should cover, and up to how much.</p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Coverage Type */}
        <div className="space-y-3">
          <Label>Coverage Type</Label>
          <div className="flex gap-6">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="dental-employer"
                checked={value.coverageTypes?.includes('employer_sponsored')}
                onCheckedChange={() => toggleCoverageType('employer_sponsored')}
              />
              <label htmlFor="dental-employer" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Employer Sponsored
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="dental-voluntary"
                checked={value.coverageTypes?.includes('voluntary')}
                onCheckedChange={() => toggleCoverageType('voluntary')}
              />
              <label htmlFor="dental-voluntary" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Voluntary
              </label>
            </div>
          </div>
        </div>

        {/* Network Type */}
        <div className="space-y-2">
          <Label htmlFor="network-type">Network Type</Label>
          <Select value={value.networkType} onValueChange={(v) => updateField('networkType', v)}>
            <SelectTrigger id="network-type">
              <SelectValue placeholder="Select network type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="dhmo">DHMO (Dental HMO)</SelectItem>
              <SelectItem value="dppo">DPPO (Dental PPO)</SelectItem>
              <SelectItem value="dual">Dual Option</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Maximum Benefit */}
        <div className="space-y-2">
          <Label htmlFor="max-benefit">Maximum Benefit</Label>
          <div className="grid grid-cols-2 gap-4">
            <Select
              value={value.maximumBenefit?.toString()}
              onValueChange={(v) => updateField('maximumBenefit', v === 'custom' ? 0 : parseInt(v))}
            >
              <SelectTrigger id="max-benefit">
                <SelectValue placeholder="Select maximum" />
              </SelectTrigger>
              <SelectContent>
                {MAXIMUM_OPTIONS.map(amount => (
                  <SelectItem key={amount} value={amount.toString()}>
                    ${amount.toLocaleString()}
                  </SelectItem>
                ))}
                <SelectItem value="custom">Custom Amount</SelectItem>
              </SelectContent>
            </Select>
            {value.maximumBenefit === 0 && (
              <Input
                type="number"
                placeholder="Enter custom amount"
                value={value.customMaximum || ''}
                onChange={(e) => updateField('customMaximum', parseInt(e.target.value) || 0)}
              />
            )}
          </div>
        </div>

        {/* Perio & Endo Coverage */}
        <div className="space-y-2">
          <Label>Perio & Endo Coverage</Label>
          <RadioGroup value={value.perioEndoCoverage} onValueChange={(v) => updateField('perioEndoCoverage', v)}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="basic" id="perio-basic" />
              <Label htmlFor="perio-basic" className="font-normal">Include in Basic</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="major" id="perio-major" />
              <Label htmlFor="perio-major" className="font-normal">Include in Major</Label>
            </div>
          </RadioGroup>
        </div>

        {/* Orthodontic Coverage */}
        <div className="space-y-2">
          <Label>Orthodontic Coverage Option</Label>
          <RadioGroup value={value.orthodonticCoverage} onValueChange={(v) => updateField('orthodonticCoverage', v)}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="adult_child" id="ortho-both" />
              <Label htmlFor="ortho-both" className="font-normal">Adult and Child</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="child_only" id="ortho-child" />
              <Label htmlFor="ortho-child" className="font-normal">Child Only</Label>
            </div>
          </RadioGroup>
        </div>

        {/* Orthodontic Maximum */}
        <div className="space-y-2">
          <Label htmlFor="ortho-max">Orthodontic Maximum</Label>
          <div className="grid grid-cols-2 gap-4">
            <Select
              value={value.orthodonticMaximum?.toString()}
              onValueChange={(v) => updateField('orthodonticMaximum', v === 'custom' ? 0 : parseInt(v))}
            >
              <SelectTrigger id="ortho-max">
                <SelectValue placeholder="Select maximum" />
              </SelectTrigger>
              <SelectContent>
                {ORTHO_MAXIMUM_OPTIONS.map(amount => (
                  <SelectItem key={amount} value={amount.toString()}>
                    ${amount.toLocaleString()}
                  </SelectItem>
                ))}
                <SelectItem value="custom">Custom Amount</SelectItem>
              </SelectContent>
            </Select>
            {value.orthodonticMaximum === 0 && (
              <Input
                type="number"
                placeholder="Enter custom amount"
                value={value.customOrthoMaximum || ''}
                onChange={(e) => updateField('customOrthoMaximum', parseInt(e.target.value) || 0)}
              />
            )}
          </div>
        </div>

        {/* Special Message */}
        <div className="space-y-2">
          <Label htmlFor="special-message">Special Message (Optional)</Label>
          <Textarea
            id="special-message"
            placeholder="Add any additional notes or requirements..."
            value={value.specialMessage || ''}
            onChange={(e) => updateField('specialMessage', e.target.value)}
            rows={4}
          />
        </div>
      </CardContent>
    </Card>
  );
}
